// StateCandidatesPage.js
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import StateCandidates from './StateCandidates';

import './MemberDetailPage.css';
import './StateCandidates.css';

/**
 * StateCandidatesPage
 *
 * Route level wrapper for StateCandidates, mounted at
 * /us/candidates/:stateAbbr.
 *
 * StateCandidates takes props, and a route element receives none - this reads
 * the state out of the URL and owns the chamber switch, so the list component
 * stays reusable anywhere else it is dropped in.
 *
 * The state lives in the path rather than in component state so the page is
 * linkable: /us/candidates/AL is a real address someone can paste or share.
 * The chamber is deliberately NOT in the path - it is a view toggle, and
 * putting it there would mean two URLs for the same place.
 */

const CHAMBERS = ['House', 'Senate'];

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico', VI: 'Virgin Islands',
};

function StateCandidatesPage() {
  const { t } = useTranslation('common');
  const { stateAbbr } = useParams();

  const [chamber, setChamber] = useState('House');

  const normalized = String(stateAbbr || '').toUpperCase();
  const stateName = STATE_NAMES[normalized];

  if (!stateName) {
    return (
      <div id="feedDisplay" className="member-detail-page">
        <div className="member-page-content">
          <p className="member-detail-text">
            {t('common:unknownState', 'No state matches that code.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div id="feedDisplay" className="member-detail-page">
      <div className="member-page-content">
        <div className="member-section-heading member-section-heading--center">
          {t('common:stateCandidatesTitle', '{{state}} candidates', { state: stateName })}
        </div>

        <div className="member-tabs member-tabs--center" role="tablist">
          {CHAMBERS.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={chamber === option}
              className={`member-tab ${chamber === option ? 'member-tab--active' : ''}`}
              onClick={() => setChamber(option)}
            >
              {option === 'House'
                ? t('common:usHouse', 'U.S. House')
                : t('common:usSenate', 'U.S. Senate')}
            </button>
          ))}
        </div>

        {/* Keyed on the pair so switching chamber remounts the list rather than
            briefly showing the previous chamber's rows under the new heading. */}
        <StateCandidates
          key={`${normalized}-${chamber}`}
          stateAbbreviation={normalized}
          candidateType={chamber}
        />
      </div>

      <style>{`
        /* Match the Glance column width on the homepage so cards read as cards
           rather than full-width bars on wide desktop screens. */
        .member-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
}

export default StateCandidatesPage;